import React from 'react';
import { GroupByOption, SortByOption } from '../types';

interface DashboardControlsProps {
    groupBy: GroupByOption;
    handleGroupByChange: (value: string) => void;
    sortBy: SortByOption;
    handleSortByChange: (value: string) => void;
    sortAsc: boolean;
    setSortAsc: (asc: boolean) => void;
    groupByOptions: any[];
    sortByOptions: any[];
    selectedTagNameForGrouping: string | null;
    selectedTagNameForSorting: string | null;
}

const DashboardControls: React.FC<DashboardControlsProps> = ({
    groupBy,
    handleGroupByChange,
    sortBy,
    handleSortByChange,
    sortAsc,
    setSortAsc,
    groupByOptions,
    sortByOptions,
    selectedTagNameForGrouping,
    selectedTagNameForSorting
}) => {

    // Helper function to get the icon based on submenu type and option
    const getOptionIcon = (submenuType: string, optionValue: string) => {
        if (submenuType === 'tags-submenu') return 'fas fa-tag';
        if (submenuType === 'events-submenu') {
            if (optionValue === 'alerts') return 'fas fa-exclamation-triangle';
            if (optionValue === 'changes') return 'fas fa-exchange';
            if (optionValue === 'custom') return 'fas fa-calendar';
            return 'fas fa-bell';
        }
        return '';
    };

    const isValidTag = (tagName: string | null): boolean => {
        return groupByOptions.find(option => option.value === 'tags-submenu')?.options?.some((tagOption: any) => tagOption.value === `tag:${tagName}`) ?? false;
    }

    return (
        <div className="estate-controls-bar d-flex align-items-center mb-3">
            {/* Group By Control */}
            <div className="group-by-control d-flex align-items-center me-2">
                <span className="me-1">Group by</span>
                <div className="dropdown">
                    <button
                        className="btn btn-outline-secondary btn-sm dropdown-toggle"
                        type="button"
                        id="groupByDropdownBs"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        style={{ minWidth: '150px' }}
                        title="Group By"
                    >
                        {groupBy === 'tag' && isValidTag(selectedTagNameForGrouping)
                            ? `Tag: ${selectedTagNameForGrouping}`
                            : groupBy === 'alerts' || groupBy === 'changes' || groupBy === 'custom'
                                ? `Event: ${groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}`
                                : groupByOptions.find(opt => (groupBy === 'tag' ? `tag:${selectedTagNameForGrouping}` : groupBy) === opt.value)?.label || 'Select Grouping'}
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="groupByDropdownBs">
                        {groupByOptions.map((option, index) => {
                            if ('options' in option && Array.isArray((option as any).options)) {
                                const subOptions = (option as any).options;
                                return (
                                    <li key={option.value} className="dropdown-submenu position-relative">
                                        <button className="dropdown-item d-flex justify-content-between align-items-center" type="button" tabIndex={-1} style={{ minWidth: 0 }}>
                                            <span>
                                                <i className={`${getOptionIcon(option.value, '')} me-1`} aria-hidden="true"/>
                                                {option.label}
                                            </span>
                                            <span className="ms-2"><i className="fas fa-angle-right"></i></span>
                                        </button>
                                        <ul className="dropdown-menu submenu-menu" style={{ display: 'none', position: 'absolute', left: '100%', top: 0, minWidth: '180px', zIndex: 1051 }}>
                                            {subOptions.map((subOption: any) => (
                                                <li key={subOption.value}>
                                                    <button
                                                        className={`dropdown-item ${
                                                            option.value === 'tags-submenu'
                                                                ? (groupBy === 'tag' && `tag:${selectedTagNameForGrouping}` === subOption.value ? 'active' : '')
                                                                : (groupBy === subOption.value ? 'active' : '')
                                                        }`}
                                                        type="button"
                                                        onClick={() => handleGroupByChange(subOption.value)}
                                                    >
                                                        <i className={`${getOptionIcon(option.value, subOption.value)} me-1`} aria-hidden="true"/>
                                                        {subOption.label}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                );
                            }
                            return (
                                <li key={option.value || `divider-${index}`}>
                                    {option.divider ? (
                                        <hr className="dropdown-divider"/>
                                    ) : (
                                        <button
                                            className={`dropdown-item ${(groupBy === 'tag' ? `tag:${selectedTagNameForGrouping}` : groupBy) === option.value ? 'active' : ''}`}
                                            type="button"
                                            onClick={() => handleGroupByChange(option.value)}
                                        >
                                            {option.value.startsWith('tag:') && <i className="fa-regular fa-tag me-1"></i>}
                                            {option.label}
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>

            {/* Sort By Control */}
            <div className="sort-by-control d-flex align-items-center me-2">
                <span className="me-1">Sort by</span>
                <div className="dropdown">
                    <button
                        className="btn btn-outline-secondary btn-sm dropdown-toggle"
                        type="button"
                        id="sortByDropdownBs"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        style={{ minWidth: '150px' }}
                        title="Sort By"
                    >
                        {sortBy === 'tag' && isValidTag(selectedTagNameForSorting)
                            ? `Tag: ${selectedTagNameForSorting}`
                            : sortBy === 'alerts' || sortBy === 'changes' || sortBy === 'custom'
                                ? `Event: ${sortBy.charAt(0).toUpperCase() + sortBy.slice(1)}`
                                : sortByOptions.find(opt => (sortBy === 'tag' ? `tag:${selectedTagNameForSorting}` : sortBy) === opt.value)?.label || 'Sort By'}
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="sortByDropdownBs">
                        {sortByOptions.map((option, index) => {
                            if ('options' in option && Array.isArray((option as any).options)) {
                                const subOptions = (option as any).options;
                                return (
                                    <li key={option.value} className="dropdown-submenu position-relative">
                                        <button className="dropdown-item d-flex justify-content-between align-items-center" type="button" tabIndex={-1} style={{ minWidth: 0 }}>
                                            <span>
                                                <i className={`${getOptionIcon(option.value, '')} me-1`} aria-hidden="true"/>
                                                {option.label}
                                            </span>
                                            <span className="ms-2"><i className="fas fa-angle-right"></i></span>
                                        </button>
                                        <ul className="dropdown-menu submenu-menu" style={{ display: 'none', position: 'absolute', left: '100%', top: 0, minWidth: '180px', zIndex: 1051 }}>
                                            {subOptions.map((subOption: any) => (
                                                <li key={subOption.value}>
                                                    <button
                                                        className={`dropdown-item ${
                                                            option.value === 'tags-submenu'
                                                                ? (sortBy === 'tag' && `tag:${selectedTagNameForSorting}` === subOption.value ? 'active' : '')
                                                                : (sortBy === subOption.value ? 'active' : '')
                                                        }`}
                                                        type="button"
                                                        onClick={() => handleSortByChange(subOption.value)}
                                                    >
                                                        <i className={`${getOptionIcon(option.value, subOption.value)} me-1`} aria-hidden="true"/>
                                                        {subOption.label}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                );
                            }
                            return (
                                <li key={option.value || `divider-${index}`}>
                                    {option.divider ? (
                                        <hr className="dropdown-divider"/>
                                    ) : (
                                        <button
                                            className={`dropdown-item ${(sortBy === 'tag' ? `tag:${selectedTagNameForSorting}` : sortBy) === option.value ? 'active' : ''}`}
                                            type="button"
                                            onClick={() => handleSortByChange(option.value)}
                                        >
                                            {option.value.startsWith('tag:') && <i className="fa-regular fa-tag me-1"></i>}
                                            {option.label}
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
                {/* Sort Direction Button */}
                <button
                    type="button"
                    className="btn btn-sm btn-icon-only ms-1 sort-direction-button"
                    onClick={() => setSortAsc(!sortAsc)}
                    aria-label={sortAsc ? "Sort Ascending" : "Sort Descending"}
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title={sortAsc ? "Ascending" : "Descending"}
                >
                    <i className={`fas ${sortAsc ? 'fa-sort-amount-up-alt' : 'fa-sort-amount-down'}`}></i>
                </button>
            </div>
        </div>
    );
};

export default DashboardControls; 